import { ParcelResultsComponent } from '@/app/modules/ppt/components/results/ppt-lookup-results.component';
import { PPTLookupService } from '@/app/modules/ppt/services/ppt-lookup.service';
import { selectFeedbackEnabled } from '@/app/state/app-state/app-state.selectors';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BTaxLookupService } from '@btax/services/btax-lookup.service';
import { sync } from '@ces/sync';
import { Platform } from '@egovsolutions/angular-platform';
import { select, Store } from '@ngrx/store';
import { NullLists } from '@shared/services/null-lists.service';
import { OnEntered, OnEntering, OnLeaving } from '@shared/services/transitions.service';
import { Viewport } from '@shared/services/viewport.service';
import { State } from '@state/model';
import { BTaxLookupNetworkService, PPTLookupNetworkService, SortInfo, Suggestion } from 'egov-api';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { selectLookupAvailability, selectLookupFindOutMore, selectLookupHasFindOutMore, selectLookupServiceAvailable, selectLookupServiceInstructions, selectLookupServiceMessages, selectLookupServices, selectLookupState, selectLookupStateLoaded, selectLookupUnavailabilityMessage } from '../../state/lookup-state/lookup-state.selectors';


// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'app-lookup',
    templateUrl: './lookup.component.html',
    styleUrls: ['./lookup.component.scss'],
    providers:
    [
        PPTLookupNetworkService,
        PPTLookupService,
        BTaxLookupNetworkService,
        BTaxLookupService,
    ],
})
export class LookupComponent implements OnInit, OnLeaving, OnEntering, OnEntered, OnDestroy
{

    constructor
    (
        // Dependencies

        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly store: Store<State>,
        private readonly pptLookupService: PPTLookupService,
        private readonly btaxLookupService: BTaxLookupService,


        // Template dependencies

        protected nullLists: NullLists,
        protected viewport: Viewport,
        protected platform: Platform,
    )
    {
        // Bindings

        this.getSuggestions = this.getSuggestions.bind(this);
        this.updateFromPath = this.updateFromPath.bind(this);
    }


    // Initialization

    public ngOnInit()
    {
        this.initializeSuggestionsHandling();
        this.initializeNavigationHandling();
        this.initializeStateLoadedHandling();
    }


    // Basic lookup state

    protected readonly currentQuery$ = new BehaviorSubject<string | undefined>(undefined);
    protected currentSort: SortInfo | null = null;
    protected readonly currentTax$ = new BehaviorSubject<string | undefined>(undefined);

    protected readonly selectedTax$ = new BehaviorSubject<string | undefined>(undefined);


    // Services availability and settings

    protected readonly stateLoaded$ = this.store.select(selectLookupStateLoaded);
    protected readonly serviceIsUnavailable$ = this.store.select(selectLookupAvailability).pipe(
        map(a => a === undefined ? a : !a)
    );
    protected readonly selectedTaxServiceIsUnavailable$ = this.selectedTax$.pipe(
        switchMap(tax => tax
            ? this.store.pipe(
                select(selectLookupServiceAvailable(tax)),
                map(a => a === undefined ? a : !a)
            )
            : of(undefined)
        )
    );

    protected readonly lookupUnavailabilityMessage$ = this.store.select(selectLookupUnavailabilityMessage);
    protected readonly fieldInstructions$ = this.selectedTax$.pipe(
        switchMap(tax => tax
            ? this.store.select(selectLookupServiceInstructions(tax))
            : of(undefined)
        ),
    );

    protected readonly feedbackEnabled$ = this.store.select(selectFeedbackEnabled);


    // Lookup state

    protected readonly showPage$ = combineLatest([
        this.serviceIsUnavailable$,
        this.selectedTaxServiceIsUnavailable$,
        this.currentQuery$
    ]).pipe(
        map(([serviceIsUnavailable, selectedTaxServiceIsUnavailable, currentQuery]) =>
            !serviceIsUnavailable && !selectedTaxServiceIsUnavailable && !!currentQuery
        )
    );

    protected readonly loadingResults$ = new BehaviorSubject(false);

    protected lookupStarted() { this.loadingResults$.next(true); }
    protected resultsUpdated() { this.loadingResults$.next(false); }
    protected lookupError() { this.loadingResults$.next(false); }

    protected changeSort(newSort: SortInfo | null)
    {
        this.navigateTo(
            this.currentTax$.value!,
            this.currentQuery$.value,
            newSort?.direction ? newSort : undefined
        );
    }


    // Tax option

    private readonly allTaxOptions$ = new BehaviorSubject<LookupTaxOptionDefinition[]>([
        {
            id: 'property-tax',
            name: 'Property Tax',
            suggestions$: this.pptLookupService.getSuggestions,
            disclaimer$: this.pptLookupService.disclaimer$,
        },
        {
            id: 'business-tax',
            name: 'Business Tax',
            suggestions$: this.btaxLookupService.getSuggestions,
            disclaimer$: this.btaxLookupService.disclaimer$,
        },
    ]);

    protected taxOptions$ = combineLatest([
        this.allTaxOptions$,
        this.store.select(selectLookupServices)
    ]).pipe(
        map(([allTaxOptions, services]) => services &&
            allTaxOptions.filter(taxOption => services.find(s => s.id === taxOption.id))
        ),
    );

    protected readonly currentTaxOption$ = combineLatest([this.currentTax$, this.taxOptions$]).pipe(
        map(([currentTax, taxOptions]) => taxOptions?.find(option => option.id === currentTax)),
    );

    private readonly selectedTaxOption$ = combineLatest([this.selectedTax$, this.taxOptions$]).pipe(
        map(([id, taxOptions]) => taxOptions?.find(option => option.id === id)),
        distinctUntilChanged(),
    );

    protected readonly selectedTaxIndex$ = combineLatest([this.selectedTaxOption$, this.taxOptions$]).pipe(
        map(([option, taxOptions]) => option && taxOptions?.indexOf(option))
    );

    protected selectTax(taxOption: string)
    {
        this.selectedTax$.next(taxOption);

        if (!this.currentQuery$.value?.trim())
            this.router.navigateByUrl(`/lookup/${taxOption}`, { replaceUrl: true });
    }

    protected taxOptionsExpanded = false;
    private taxOptionsJustOpened = false;

    protected expandTaxOptions(calledOnClick = true)
    {
        if (calledOnClick) this.taxOptionsJustOpened = true;
        this.taxOptionsExpanded = true;
    }

    @HostListener('window:click', ['$event']) protected clickHandler(e: MouseEvent)
    {
        if (this.taxOptionsJustOpened)
            this.taxOptionsJustOpened = false;
        else
        {
            if (this.taxOptionsExpanded)
                this.taxOptionsExpanded = false;
        }
    }


    // Messages

    protected readonly serviceMessages$ = this.selectedTax$.pipe(
        switchMap(id => id ? this.store.select(selectLookupServiceMessages(id)) : of(undefined))
    );

    private readonly serviceHasMessages$ = this.serviceMessages$.pipe(map(messages => !!messages?.length));

    protected showMessages$ = combineLatest([
        this.serviceHasMessages$,
        this.showPage$,
        this.currentTax$,
        this.selectedTax$,
    ]).pipe(
        map(([hasMessages, showPage, currentTax, selectedTax]) =>
            !!hasMessages
            &&
            (
                !showPage
                ||
                currentTax !== selectedTax
            )
        )
    );


    // Find out more

    protected readonly hasMoreInfo$ = this.store.select(selectLookupHasFindOutMore);
    protected readonly moreInfo$ = this.store.select(selectLookupFindOutMore).pipe(map(mi => mi || this.nullLists.get(3)));


    // Navigation

    private navigateTo(taxOptionId: string, query?: string, sort?: { active: string, direction: string })
    {
        this.router.navigateByUrl(this.getPath(taxOptionId, query, sort));
    }

    private getPath(taxOptionId: string, query?: string, sort?: { active: string, direction: string })
    {
        let path = `lookup/${taxOptionId}`;

        if (query?.trim().length)
        {
            path += '/' + encodeURIComponent(query.trim());
            if (sort) path += `/${sort.active}/${sort.direction}`;
        }

        return path;
    }

    private navigationSubscription?: Subscription;

    private initializeNavigationHandling()
    {
        this.navigationSubscription =
            this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(this.updateFromPath);

        this.updateFromPath();
    }

    private updateFromPath()
    {
        const snapshot = this.route.snapshot;
        const tax = snapshot.firstChild?.params.tax;

        this.selectedTax$.next(tax);
        this.currentTax$.next(tax);

        this.currentQuery$.next(snapshot.firstChild?.firstChild?.params.query);

        const sortRoute = snapshot.firstChild?.firstChild?.firstChild;
        if (sortRoute?.params.sort && sortRoute?.params.direction)
            this.currentSort =
            {
                active: sortRoute?.params.sort,
                direction: sortRoute?.params.direction
            };
        else
            this.currentSort = null;

        this.lookupQuery.setValue(this.currentQuery$.value ? this.currentQuery$.value : '');


        const shouldFocusField = !this.currentQuery$.value && !this.platform.touchIsMainInput$.value;

        this._autofocus = shouldFocusField;
        this.autofocused = false;
    }


    // Handle state loaded

    private stateLoadedSubscription?: Subscription;

    private initializeStateLoadedHandling()
    {
        this.stateLoadedSubscription = this.store.select(selectLookupState).pipe(
            filter(v => !!v),
            filter(s => s!.available && !!s!.lookupServices.length),
            switchMap(state => combineLatest([of(state), this.currentTax$, this.taxOptions$]))
        ).subscribe(([state, currentTax, taxOptions]) =>
        {
            if (!currentTax || !taxOptions!.find(o => o.id === currentTax))
            {
                if (state!.lookupServices.length)
                {
                    const tax = state!.lookupServices[0].id;
                    this.navigateTo(tax);
                }
                else
                    this.router.navigateByUrl('not-found');
            }
        });
    }


    // Lookup field

    @ViewChild('lookupField', { read: ElementRef }) private readonly lookupFieldRef!: ElementRef;

    protected lookupQuery: UntypedFormControl = new UntypedFormControl('');
    protected lookupForm: UntypedFormGroup = new UntypedFormGroup({ lookupQuery: this.lookupQuery });
    protected readonly lookupQueryHasFocus$ = new BehaviorSubject(false);

    private _autofocus = true;

    private autofocused?: boolean;

    protected get autofocus(): boolean
    {
        if (this._autofocus && !this.autofocused && sync(this.stateLoaded$))
            setTimeout(() =>
            {
                this.lookupFieldRef.nativeElement.focus();
                this.autofocused = true;
            },
            0);

        return this._autofocus;
    }

    protected onLookupEnter($event: KeyboardEvent)
    {
        $event.preventDefault();
        ($event.target as HTMLInputElement).blur();

        if (this.currentQuery$.value === this.lookupQuery.value)
            this.retry();

        this.navigateTo(sync(this.selectedTax$)!, this.lookupQuery.value);
    }


    // Suggestions

    @ViewChild('suggestionsContainer', { read: ElementRef })
    private readonly suggestionsContainer!: ElementRef;

    protected suggestions?: Suggestion[];
    protected suggestionsError = false;
    protected currentSuggestion = -1;

    protected readonly currentSuggestionsQuery$ = new BehaviorSubject<string | undefined>(undefined);

    protected readonly showSuggestions$ = combineLatest([this.currentSuggestionsQuery$, this.lookupQueryHasFocus$]).pipe(
        map(([currentSuggestionsQuery, lookupQueryHasFocus]) => !!(currentSuggestionsQuery && lookupQueryHasFocus))
    );

    protected selectPreviousSuggestion($event: KeyboardEvent)
    {
        $event.preventDefault();

        if (this.thereAreSuggestions)
        {
            this.currentSuggestion--;

            if (this.currentSuggestion < 0)
                this.currentSuggestion = this.suggestions!.length - 1;

            this.ignoreOneLookupUpdate = true;

            this.lookupQuery.setValue(this.suggestions![this.currentSuggestion].value);
        }
    }

    protected selectNextSuggestion($event: KeyboardEvent)
    {
        $event.preventDefault();

        if (this.thereAreSuggestions)
        {
            this.currentSuggestion++;

            if (this.currentSuggestion >= this.suggestions!.length)
                this.currentSuggestion -= this.suggestions!.length;

            this.ignoreOneLookupUpdate = true;

            this.lookupQuery.setValue(this.suggestions![this.currentSuggestion].value);
        }
    }

    protected onSuggestionClick(index: number)
    {
        this.navigateTo(sync(this.selectedTax$)!, this.suggestions![index].value);
    }


    // Get suggestions

    private lookupFieldChangeSubscription?: Subscription;

    private initializeSuggestionsHandling()
    {
        this.lookupFieldChangeSubscription = this.lookupForm.valueChanges.pipe(
            debounceTime(300),
            map(formValue => formValue.lookupQuery),
            map(text => text?.trim()),
            distinctUntilChanged(),
        )
        .subscribe(this.getSuggestions);
    }

    private ignoreOneLookupUpdate = false;

    private suggestionsSubscription?: Subscription;

    private getSuggestions(value: string)
    {
        if (!this.ignoreOneLookupUpdate)
        {
            if (this.currentSuggestionsQuery$.value !== value)
            {
                this.currentSuggestionsQuery$.next(value);

                if (this.suggestionsSubscription)
                    this.suggestionsSubscription.unsubscribe();

                delete this.suggestions;
                this.suggestionsError = false;
                this.currentSuggestion = -1;

                if (value)
                    this.suggestionsSubscription = this.selectedTaxOption$.pipe(
                        filter(v => !!v),
                        switchMap(taxOption => taxOption!.suggestions$(this.lookupQuery.value)),
                    ).subscribe({
                        next: suggestions => this.suggestions = suggestions,
                        error: error => this.suggestionsError = true,
                    });
            }
        }
        else
            this.ignoreOneLookupUpdate = false;
    }

    protected get thereAreSuggestions(): boolean
    { return !!(this.lookupQuery.value && this.suggestions && this.suggestions.length); }


    // Retry on enter

    @ViewChild(ParcelResultsComponent) protected parcelResults?: ParcelResultsComponent;

    private retry()
    {
        if (this.parcelResults) this.parcelResults.retry();
    }



    // Field vertical centering

    @ViewChild('formCenterer', { read: ElementRef }) private readonly formCenterer!: ElementRef;

    protected readonly centerFieldVertically$ = combineLatest([
        this.serviceIsUnavailable$,
        this.currentQuery$,
        this.currentSuggestionsQuery$,
        this.serviceHasMessages$,
    ]).pipe(
        map(([serviceIsUnavailable, currentQuery, currentSuggestionsQuery, hasMessages]) =>

            serviceIsUnavailable

            ||

            !currentQuery
            &&
            !currentSuggestionsQuery
            &&
            !hasMessages

        )
    );

    protected get suggestionsExcess(): string | undefined
    {
        if (!this.currentQuery$.value)
        {
            if
            (
                this.formCenterer?.nativeElement
                &&
                this.suggestionsContainer
                &&
                this.suggestionsContainer.nativeElement
            )
            {
                const formCentererBottom = (this.formCenterer.nativeElement as HTMLElement).getClientRects()[0].bottom;
                const suggestionsContainerBottom =
                    (this.suggestionsContainer.nativeElement as HTMLElement).getClientRects()[0].bottom;

                if (suggestionsContainerBottom > formCentererBottom)
                    return suggestionsContainerBottom - formCentererBottom + 'px';
            }
        }
    }


    // Prevent BG from jumping due to page transitions

    protected transitionScrollTop = 0;

    public onLeaving(scroll: [number, number])
    {
        this.transitionScrollTop = scroll[1];
    }

    public onTransitionCancelled()
    {
        this.transitionScrollTop = 0;
    }

    public onEntering(scroll: [number, number])
    {
        this.transitionScrollTop = scroll[1];
    }

    public onEntered()
    {
        this.transitionScrollTop = 0;
    }


    // Disclaimer

    protected readonly disclaimer$ = this.currentTaxOption$.pipe(
        switchMap(option => option
            ? option.disclaimer$
            : of(option)
        )
    );


    // Clear subscriptions

    public ngOnDestroy()
    {
        this.lookupFieldChangeSubscription!.unsubscribe();
        this.navigationSubscription!.unsubscribe();
        this.stateLoadedSubscription!.unsubscribe();

        if (this.suggestionsSubscription) this.suggestionsSubscription.unsubscribe();
    }
}

interface LookupTaxOptionDefinition
{
    id: string
    name: string
    suggestions$: (query: string) => Observable<Suggestion[]>
    disclaimer$: Observable<string | undefined>
}
